import React from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useNavigation } from '@react-navigation/native';
import { MaintenanceDateLabel } from './MaintenanceDateLabel';
import { TicketTypes } from './MaintenanceEmptyState';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import {
  getStatusColor,
  getStatusLabel,
  getCategoryName,
} from '@/utils/residents/maintenance';

interface MaintenanceCardProps {
  ticket: IMaintenanceListResponse;
}

export const MaintenanceCard: React.FC<MaintenanceCardProps> = ({ ticket }) => {
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const navigation = useNavigation<any>();

  // Use utilities for status info
  const statusBackgroundColor = getStatusColor(ticket);
  const statusText = getStatusLabel(ticket);

  if (!isDesktop) {
    // Mobile view - simpler card layout
    const CardComponent: any = TouchableOpacity;
    const cardProps: any = { onPress: () => navigation?.navigate?.('MaintenanceDetail', { ticket }) };
    return (
      <CardComponent style={styles.mobileCard} {...cardProps}>
        <View style={styles.mobileHeader}>
          <View style={[styles.statusBadge, { backgroundColor: statusBackgroundColor }]}>
            <Text style={styles.statusText}>{statusText}</Text>
          </View>
          <MaintenanceDateLabel
            ticketType={ticket.completedOn ? TicketTypes.SOLVED : TicketTypes.OPEN}
            createdOn={ticket.createdOn}
            updatedOn={ticket.updatedOn}
            completedOn={ticket.completedOn}
          />
        </View>
        <Text style={styles.mobileTitle}>Ticket #{ticket.maintenanceRequestId}</Text>
        <Text style={styles.mobileDescription} numberOfLines={3}>
          {ticket.maintenanceDescription}
        </Text>
      </CardComponent>
    );
  }

  // Desktop view - community resources card style
  return (
    <View style={styles.desktopCard}>
      {/* Status badge row */}
      <View style={styles.statusRow}>
        <View style={[styles.statusBadge, { backgroundColor: statusBackgroundColor }]}>
          <Text style={styles.statusText}>{statusText}</Text>
        </View>
        <MaintenanceDateLabel
          ticketType={ticket.completedOn ? TicketTypes.SOLVED : TicketTypes.OPEN}
          createdOn={ticket.createdOn}
          updatedOn={ticket.updatedOn}
          completedOn={ticket.completedOn}
        />
      </View>

      {/* Title and date row */}
      <View style={styles.titleRow}>
        <Text style={styles.requestTitle} numberOfLines={1}>
          Request #{ticket.maintenanceRequestId}
        </Text>
        {/* Intentionally omit right-side date to match web; date is shown in pill above */}
      </View>

      {/* Details grid */}
      <View style={styles.detailsGrid}>
        <View style={styles.detailRow}>
          <RESIDENTS_SVG_COMPONENTS.LOCATION width={14} height={14} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE} />
          <Text style={styles.detailText} numberOfLines={1}>{ticket.maintenanceLocation}</Text>
        </View>
        
        <View style={styles.detailRow}>
          <RESIDENTS_SVG_COMPONENTS.WRENCH width={14} height={14} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE} />
          <Text style={styles.detailText} numberOfLines={1}>
            {getCategoryName(ticket, 'General')}
          </Text>
        </View>
        
        <View style={styles.detailRow}>
          <RESIDENTS_SVG_COMPONENTS.SPEED width={14} height={14} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE} />
          <Text style={styles.detailText} numberOfLines={1}>{ticket.maintenancePriority}</Text>
        </View>
      </View>

      {/* Description */}
      <Text style={styles.descriptionText} numberOfLines={3}>
        {ticket.maintenanceDescription}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  // Mobile styles
  mobileCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  mobileHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  mobileDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  mobileTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  mobileDescription: {
    fontSize: 14,
    color: '#4B5563',
    lineHeight: 20,
  },

  // Desktop styles - matching resio-web BaseCard
  desktopCard: {
    borderRadius: 16, // matches resio-web rounded-2xl
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    padding: 24, // matches resio-web p-6 (24px)
    marginBottom: 16,
    // Ensure the card is tall enough to display all sections
    minHeight: 220,
    width: '100%',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8, // matches resio-web space-y-2
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2, // matches resio-web py-0.5
    borderRadius: 12,
    marginRight: 8,
  },
  statusText: {
    fontSize: 12, // matches resio-web text-xs
    fontWeight: '600',
    color: RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY,
    textTransform: 'capitalize',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12, // matches resio-web mb-3
  },
  requestTitle: {
    fontSize: 16,
    fontWeight: '600', // matches resio-web font-semibold
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    flex: 1,
  },
  dateText: {
    fontSize: 12,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    marginLeft: 8,
  },
  detailsGrid: {
    gap: 4, // matches resio-web mb-1 spacing
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  detailText: {
    fontSize: 14, // matches resio-web text-sm
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    flex: 1,
  },
  descriptionText: {
    fontSize: 12, // matches resio-web text-xs
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    lineHeight: 18,
    marginTop: 12, // add some separation like resio-web border-t spacing
    paddingTop: 12, // matches the divider spacing in resio-web
    borderTopWidth: 1,
    borderTopColor: '#374151', // matches resio-web border-gray-600
  },
});
